<template>
  <div
    class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
    v-if="loading"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>

  <form class="validate-form" @submit.prevent="handleSubmit" v-else>
    <div>
      <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
        <AwardIcon />
        <h2 class="text-lg font-medium mr-auto">
          {{
            mode === 'create'
              ? $t('advertisement.add')
              : mode === 'edit'
              ? $t('advertisement.edit')
              : $t('advertisement.convert')
          }}
        </h2>
        <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
          <button type="submit" class="btn btn-primary shadow-md flex items-center">
            <SaveIcon class="w-4 h-4 mr-2" />
            {{ mode === 'convert' ? $t('advertisement.convert') : $t('common.save') }}
          </button>
          <button
            type="button"
            @click="$emit('cancel')"
            class="btn shadow-md flex items-center ml-2"
          >
            <ArrowLeftCircleIcon class="w-4 h-4 mr-2" /> {{ $t('common.back') }}
          </button>
        </div>
      </div>

      <div class="pos intro-y grid grid-cols-12 gap-5 mt-5">
        <div class="col-span-12">
          <div class="intro-y box p-5">
            <!-- BUSINESS INFO -->
            <div class="mt-3">
              <label class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('public.businessName') }}
                <span class="sm:ml-auto text-xs text-gray-600">{{
                  $t('form.required')
                }}</span>
              </label>
              <input
                v-model.trim="validate.businessName.$model"
                type="text"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.businessName.$error }"
                :readonly="!initialData?.advertisement"
              />
              <div v-if="validate.businessName.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.requiredField') }}
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('public.businessId') }}
                <span class="sm:ml-auto text-xs text-gray-600">{{
                  mode === 'create' ? $t('form.optional') : $t('form.required')
                }}</span>
              </label>
              <input
                v-model.trim="validate.businessId.$model"
                type="text"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :class="{ 'border-theme-6': validate.businessId.$error }"
                :readonly="!initialData?.advertisement"
              />
            </div>

            <div class="mt-3">
              <label class="form-label w-full flex flex-col sm:flex-row">{{
                $t('public.taxId')
              }}</label>
              <input
                v-model.trim="validate.taxId.$model"
                type="text"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :readonly="!initialData?.advertisement"
              />
            </div>

            <div class="mt-3">
              <label class="form-label w-full flex flex-col sm:flex-row">{{
                $t('public.vatRegistrationId')
              }}</label>
              <input
                v-model.trim="validate.vatRegistrationId.$model"
                type="text"
                class="intro-x login__input form-control py-3 px-4 border-gray-300 block mt-4"
                :readonly="!initialData?.advertisement"
              />
            </div>

            <div class="mt-3">
              <label class="form-label">{{ $t('form.field.iban') }}</label>
              <input
                v-model.trim="validate.iban.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.iban.$error }"
                placeholder="SK00..."
                :readonly="!initialData?.advertisement"
              />
              <div v-if="validate.iban.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.ibanValidator') }}
              </div>
            </div>

            <!-- BRANCH INFO -->
            <div class="mt-3">
              <label class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.name') }}
                <span class="sm:ml-auto text-xs text-gray-600">{{
                  $t('form.required')
                }}</span>
              </label>
              <input
                v-model.trim="validate.name.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.name.$error }"
              />
              <div v-if="validate.name.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.name.required') }}
              </div>
            </div>

            <!-- TABS (Languages) -->
            <div class="post intro-y overflow-hidden box mt-5">
              <div
                class="post__tabs nav nav-tabs flex-col sm:flex-row bg-gray-300 dark:bg-dark-2 text-gray-600"
                role="tablist"
              >
                <a
                  id="sk-tab"
                  data-toggle="tab"
                  data-target="#sk"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center active"
                  role="tab"
                >
                  <country-flag country="sk" size="small" />
                  {{ $t('common.language.sk_SK') }}
                </a>
                <a
                  id="en-tab"
                  data-toggle="tab"
                  data-target="#en"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  role="tab"
                >
                  <country-flag country="gb" size="small" />
                  {{ $t('common.language.en_UK') }}
                </a>
                <a
                  id="cs-tab"
                  data-toggle="tab"
                  data-target="#cs"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  role="tab"
                >
                  <country-flag country="cz" size="small" />
                  {{ $t('common.language.cs_CZ') }}
                </a>
                <a
                  id="de-tab"
                  data-toggle="tab"
                  data-target="#de"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  role="tab"
                >
                  <country-flag country="de" size="small" />
                  {{ $t('common.language.de_DE') }}
                </a>
                <a
                  id="pl-tab"
                  data-toggle="tab"
                  data-target="#pl"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  role="tab"
                >
                  <country-flag country="pl" size="small" />
                  {{ $t('common.language.pl_PL') }}
                </a>
              </div>

              <div class="tab-content">
                <!-- Helper for tab content -->
                <div
                  v-for="lang in ['sk', 'en', 'cs', 'de', 'pl']"
                  :key="lang"
                  :id="lang"
                  class="tab-pane"
                  :class="{ active: lang === 'sk' }"
                  role="tabpanel"
                >
                  <div class="mt-3">
                    <label class="form-label">{{ $t('common.slogan') }}</label>
                    <input
                      v-model.trim="formData[`slogan${capitalize(lang)}`]"
                      type="text"
                      class="intro-y form-control py-3 px-4 box"
                    />

                    <label class="form-label mt-3">{{ $t('common.description') }}</label>
                    <textarea
                      class="intro-y form-control py-3 px-4 box"
                      v-model="descriptions[`description${capitalize(lang)}`]"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-3 text-theme-6" v-if="validate.sloganSk.$error">
              {{ $t('form.validation.gte2ch') }}
            </div>

            <!-- IMAGES -->
            <div class="mt-3" :class="{ 'border-theme-6': validate.image.$error }">
              <label class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.branchImg') }}
                <span class="sm:ml-auto text-xs text-gray-600">{{
                  $t('form.required')
                }}</span>
              </label>
              <ImageUploader
                ref="imageUploaderRef"
                :aspectRatio="1 / 1"
                :defaultImage="initialData?.branch?.image"
              />
              <div v-if="validate.image.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.imageRequired') }}
              </div>
            </div>

            <div class="mt-3" :class="{ 'border-theme-6': validate.imageWide.$error }">
              <label class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.branchImgWide') }}
                <span class="sm:ml-auto text-xs text-gray-600">{{
                  $t('form.required')
                }}</span>
              </label>
              <WideImageUploader
                ref="imageWideUploaderRef"
                :defaultImage="initialData?.branch?.imageWide"
              />
              <div v-if="validate.imageWide.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.imageRequired') }}
              </div>
            </div>

            <!-- CONTACT -->
            <div class="mt-3">
              <label class="form-label">{{ $t('common.phoneNumber') }}</label>
              <input
                v-model.trim="validate.phoneNumber.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.phoneNumber.$error }"
                placeholder="+421..."
              />
              <div v-if="validate.phoneNumber.$error" class="text-theme-6 mt-2">
                {{ $t('form.constraint.phoneFormat') }}
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label">Email</label>
              <input
                v-model.trim="validate.mail.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.mail.$error }"
              />
              <div v-if="validate.mail.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.email') }}
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label">Web</label>
              <input
                v-model.trim="validate.web.$model"
                type="text"
                class="intro-y form-control py-3 px-4 box"
                :class="{ 'border-theme-6': validate.web.$error }"
              />
              <div v-if="validate.web.$error" class="text-theme-6 mt-2">
                {{ $t('form.validation.url') }}
              </div>
            </div>

            <!-- CATEGORIES -->
            <div class="mt-3">
              <BranchCategoryMultiselect
                v-model:selectedCategories="selectedCategories"
              />
            </div>

            <!-- ADDRESS (New Component) -->
            <div class="intro-y box p-5 mt-5">
              <h3 class="font-medium text-base mb-3">{{ $t('common.address') }}</h3>
              <AddressForm v-model="validate.address.$model" ref="addressFormRef" />
            </div>

            <!-- OPEN HOURS -->
            <div class="intro-y box p-5 mt-5">
              <OpenHoursForm v-model:openHours="openHours" />
            </div>

            <!-- TIME VISIBILITY -->
            <div class="mt-3">
              <label class="form-label">{{ $t('form.field.timeFrom') }}</label>
              <input v-model="dateFromView" class="form-check-switch" type="checkbox" />
              <div v-show="dateFromView" class="mt-2">
                <Litepicker
                  v-model="dateFrom"
                  :options="litepickerOptions"
                  class="form-control pl-12 w-56 block mx-auto mb-2"
                />
                <input
                  v-model.trim="validate.timeFrom.$model"
                  type="time"
                  class="intro-y form-control py-3 px-4 box"
                  :class="{ 'border-theme-6': validate.timeFrom.$error }"
                />
                <div v-if="validate.timeFrom.$error" class="text-theme-6 mt-2">
                  {{ $t('form.validation.requiredField') }}
                </div>
              </div>
            </div>

            <div class="mt-3">
              <label class="form-label">{{ $t('form.field.timeTo') }}</label>
              <input v-model="dateToView" class="form-check-switch" type="checkbox" />
              <div v-show="dateToView" class="mt-2">
                <Litepicker
                  v-model="dateTo"
                  :options="litepickerOptions"
                  class="form-control pl-12 w-56 block mx-auto mb-2"
                />
                <input
                  v-model.trim="validate.timeTo.$model"
                  type="time"
                  class="intro-y form-control py-3 px-4 box"
                  :class="{ 'border-theme-6': validate.timeTo.$error }"
                />
                <div v-if="validate.timeTo.$error" class="text-theme-6 mt-2">
                  {{ $t('form.validation.requiredField') }}
                </div>
              </div>
            </div>

            <!-- ACTIVE -->
            <div class="form-check flex-col items-start mt-3">
              <label for="active" class="form-check-label ml-0 mb-2">{{
                $t('common.active')
              }}</label>
              <input
                id="active"
                v-model="validate.active.$model"
                class="form-check-switch"
                type="checkbox"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, toRefs, toRaw, watch, computed } from 'vue'
import { required, minLength, helpers, email, url } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Toastify from 'toastify-js'

// Components
import BranchCategoryMultiselect from '@/components/branches/BranchCategoryMultiselect.vue'
import ImageUploader from '@/components/cdn/ImageUploader.vue'
import WideImageUploader from '@/components/cdn/WideImageUploader.vue'
import AddressForm from '@/components/address-form/AddressForm.vue'
import OpenHoursForm from '@/components/branches/OpenHoursForm.vue'

const props = defineProps({
  loading: Boolean,
  mode: String, // 'create', 'edit', 'convert'
  initialData: Object
})

const emit = defineEmits(['submit', 'cancel'])

// Refs
const addressFormRef = ref(null)
const imageUploaderRef = ref(null)
const imageWideUploaderRef = ref(null)

// Models
const selectedCategories = ref([])
const openHours = ref({})

// Descriptions
const descriptions = reactive({
  descriptionSk: '',
  descriptionEn: '',
  descriptionCs: '',
  descriptionDe: '',
  descriptionPl: ''
})

// Dates
const dateConstraint = new Date() - 1
const dateFromView = ref(false)
const dateFrom = ref('')
const dateToView = ref(false)
const dateTo = ref('')

const litepickerOptions = {
  autoApply: true,
  singleMode: true,
  numberOfColumns: 2,
  numberOfMonths: 2,
  minDate: dateConstraint,
  dropdowns: { minYear: 2023, maxYear: null, months: true, years: true },
  lang: 'sk-SK'
}

// Validation Regex
const phoneNumberValidator = helpers.regex(/^\+421[0-9]{9}$/)
const urlValidator = helpers.regex(/(https?:\/\/.*\.[a-z0-9]+)/i)
const ibanValidator = helpers.regex(
  /^SK\d{2}\s?\d{4}\s?\d{4}\s?\d{2}[0-9A-Za-z]{2}\s[0-9A-Za-z]{4}\s[0-9A-Za-z]{4}?$/
)
const timeValidator = helpers.regex(/^([0-1][0-9]|2[0-4]):[0-5][0-9]$/)

const formData = reactive({
  businessName: '',
  businessId: '',
  taxId: '',
  vatRegistrationId: '',
  iban: '',
  name: '',
  sloganSk: '',
  sloganEn: '',
  sloganCs: '',
  sloganDe: '',
  sloganPl: '',
  phoneNumber: '',
  mail: '',
  web: '',
  timeFrom: '',
  timeTo: '',
  active: false,
  image: '',
  imageWide: '',
  address: {
    street: '',
    streetNumber: '',
    city: '',
    zip: '',
    latitude: null,
    longitude: null,
    state: 'SK'
  }
})

const rules = {
  businessName: {
    required: helpers.withMessage('form.validation.requiredField', required)
  },
  businessId: {
    required: helpers.withMessage('form.validation.requiredField', required)
  }, // Logic can be tweaked if optional in create
  taxId: {},
  vatRegistrationId: {},
  iban: {
    ibanValidator: helpers.withMessage('form.validation.ibanValidator', ibanValidator)
  },
  name: {
    required: helpers.withMessage('form.validation.name.required', required),
    minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
  },
  sloganSk: { minLength: helpers.withMessage('form.validation.gte2ch', minLength(2)) },
  sloganEn: { minLength: helpers.withMessage('form.validation.gte2ch', minLength(2)) },
  sloganCs: { minLength: helpers.withMessage('form.validation.gte2ch', minLength(2)) },
  sloganDe: { minLength: helpers.withMessage('form.validation.gte2ch', minLength(2)) },
  sloganPl: { minLength: helpers.withMessage('form.validation.gte2ch', minLength(2)) },
  phoneNumber: {
    required: helpers.withMessage('form.validation.requiredField', required),
    phoneNumberValidator: helpers.withMessage(
      'form.validation.phoneNumberValidator',
      phoneNumberValidator
    )
  },
  mail: { email: helpers.withMessage('form.validation.email', email) },
  web: { url: helpers.withMessage('form.validation.url', url) },
  active: {},
  image: {
    required: helpers.withMessage('form.validation.imageRequired', required),
    urlValidator: helpers.withMessage('form.validation.imageUrl', urlValidator)
  },
  imageWide: {
    required: helpers.withMessage('form.validation.imageRequired', required),
    urlValidator: helpers.withMessage('form.validation.imageUrl', urlValidator)
  },
  address: { required: helpers.withMessage('form.validation.addressRequired', required) },
  timeFrom: { timeValidator },
  timeTo: { timeValidator }
}
if (props.mode === 'create') {
  rules.businessId = {}
}

const validate = useVuelidate(rules, toRefs(formData))

// Helper
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1)

// Date Parser
const parseDateTime = (isoStr) => {
  if (!isoStr) return { date: '', time: '' }
  const d = new Date(isoStr)
  const datePart = d.toISOString().split('T')[0] // yyyy-mm-dd
  const timePart = d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
  return { date: datePart, time: timePart }
}

const combineDateTime = (isActive, dateObj, timeStr) => {
  if (!isActive || !dateObj || !timeStr) return null
  // Litepicker returns Date object or string depending on config, ensure format
  let dStr = dateObj
  if (dateObj instanceof Date) dStr = dateObj.toISOString().split('T')[0]
  // Format to ISO
  return new Date(`${dStr}T${timeStr}:00`).toISOString()
}

// Populate Logic
watch(
  () => props.initialData,
  (data) => {
    if (!data) return

    formData.businessName = data.businessName || ''
    formData.businessId = data.businessId || ''
    formData.taxId = data.taxId || ''
    formData.vatRegistrationId = data.vatRegistrationId || ''
    formData.iban = data.iban || ''

    const branch = data.branch || {}
    formData.name = branch.name || ''
    formData.phoneNumber = branch.phoneNumber || ''
    formData.mail = branch.mail || ''
    formData.web = branch.web || ''
    formData.active = branch.active || false
    formData.image = branch.image || ''
    formData.imageWide = branch.imageWide || ''

    // Slogans
    if (branch.slogan) {
      formData.sloganSk = branch.slogan.sk_SK || ''
      formData.sloganEn = branch.slogan.en_US || ''
      formData.sloganCs = branch.slogan.cs_CZ || ''
      formData.sloganDe = branch.slogan.de_DE || ''
      formData.sloganPl = branch.slogan.pl_PL || ''
    }

    // Descriptions
    if (branch.description) {
      descriptions.descriptionSk = branch.description.sk_SK || ''
      descriptions.descriptionEn = branch.description.en_US || ''
      descriptions.descriptionCs = branch.description.cs_CZ || ''
      descriptions.descriptionDe = branch.description.de_DE || ''
      descriptions.descriptionPl = branch.description.pl_PL || ''
    }

    // Complex
    if (branch.categories) selectedCategories.value.selectedCategories = branch.categories
    if (branch.address) Object.assign(formData.address, branch.address)
    if (branch.openHours) openHours.value = branch.openHours

    // Dates
    if (data.visibleFrom) {
      dateFromView.value = true
      const p = parseDateTime(data.visibleFrom)
      dateFrom.value = p.date
      formData.timeFrom = p.time
    }
    if (data.visibleTo) {
      dateToView.value = true
      const p = parseDateTime(data.visibleTo)
      dateTo.value = p.date
      formData.timeTo = p.time
    }
  },
  { deep: true, immediate: true }
)

const handleSubmit = async () => {
  // Update images from uploaders
  const img = imageUploaderRef.value?.getValue()
  formData.image = img !== null ? img : props.initialData?.branch?.image || ''

  const imgWide = imageWideUploaderRef.value?.getValue()
  formData.imageWide =
    imgWide !== null ? imgWide : props.initialData?.branch?.imageWide || ''

  // Validate
  const isFormValid = await validate.value.$validate()

  let isChildValid = false
  if (addressFormRef.value && addressFormRef.value.validate) {
    isChildValid = await addressFormRef.value.validate.$validate()
  }

  if (!isFormValid || !isChildValid) {
    console.log(validate.value.$errors)
    console.log(isChildValid)
    Toastify({
      node: cash('#failed-notification-content').clone().removeClass('hidden')[0],
      duration: 10000,
      newWindow: true,
      close: true,
      gravity: 'top',
      position: 'right',
      stopOnFocus: true,
      text: 'Validation Error'
    }).showToast()
    return
  }

  const payload = {
    businessName: formData.businessName,
    businessId: formData.businessId,
    taxId: formData.taxId,
    vatRegistrationId: formData.vatRegistrationId,
    iban: formData.iban,
    visibleFrom: combineDateTime(dateFromView.value, dateFrom.value, formData.timeFrom),
    visibleTo: combineDateTime(dateToView.value, dateTo.value, formData.timeTo),
    branch: {
      name: formData.name,
      image: formData.image,
      imageWide: formData.imageWide,
      phoneNumber: formData.phoneNumber,
      mail: formData.mail,
      web: formData.web,
      active: formData.active,
      slogan: {
        sk_SK: formData.sloganSk,
        en_US: formData.sloganEn,
        cs_CZ: formData.sloganCs,
        de_DE: formData.sloganDe,
        pl_PL: formData.sloganPl
      },
      description: {
        sk_SK: descriptions.descriptionSk,
        en_US: descriptions.descriptionEn,
        cs_CZ: descriptions.descriptionCs,
        de_DE: descriptions.descriptionDe,
        pl_PL: descriptions.descriptionPl
      },
      categories: toRaw(selectedCategories.value.selectedCategories) ?? [],
      address: toRaw(formData.address),
      openHours: toRaw(openHours.value)
    }
  }

  emit('submit', payload)
}
</script>
